import { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import LogoutModel from "../components/model/LogoutModel";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../utils/keys";
import { strings } from "../strings/strings";
import { useQueryClient } from "@tanstack/react-query";
import { useAppQuery } from "../api/fetchClient";
import { GET_REQUESTS } from "../api/url";
import { QUERY_KEYS } from "../api/queryKeys";

const DashboardLayout = ({ menuItems, logo, profileImg, roleName }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [adminData, setAdminData] = useState({});
  const location = useLocation();
  const dropdownRef = useRef(null);
  const [isLogout, setIsLogout] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const shouldFetchCount = location.pathname.startsWith("/country");

  const { data: requestsData } = useAppQuery({
    queryKey: [QUERY_KEYS.REQUESTS, "count"],
    url: `${GET_REQUESTS}?page=1&limit=1`,
    method: "GET",
    enabled: shouldFetchCount,
  });

  const requestCount =
    requestsData?.results?.totalItems || requestsData?.results?.pagination?.totalItems || 0;

  const handleConfirmLogOut = async () => {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem("role");
    localStorage.removeItem(strings.adminData);
    localStorage.removeItem("otp_verified");

    queryClient.clear();

    setIsLogout(false);
    navigate("/login", { replace: true });
  };

  const handleCancelLogout = () => {
    setIsLogout(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSidebarOpen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const data = localStorage.getItem(strings.adminData);
    try {
      setAdminData(JSON.parse(data) || {});
    } catch {
      setAdminData({});
    }
  }, []);

  const headerDisplayName = adminData?.username || "";

  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [location, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <div className="h-screen flex flex-col bg-grey overflow-hidden">
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20" onClick={() => setIsSidebarOpen(false)} />
      )}

      <header className="bg-white shadow-sm fixed w-full z-30 h-[4rem]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} aria-label="Toggle sidebar">
              <img src={logo.menu} alt="menu" />
            </button>
            <img src={logo.main} alt="Logo" className="h-8" />
          </div>

          <div className="flex items-center gap-4">
         
            {location.pathname.startsWith("/country") && (
              <Link
                to="/country/requests"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-black bg-grey rounded-lg hover:bg-primary/90 hover:text-white transition-colors relative"
              >
                <span>{strings.requests}</span>
                {requestCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                    {requestCount}
                  </span>
                )}
              </Link>
            )}
            
            <div className="relative">
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2">
                <img src={profileImg} alt="Admin" className="w-8 h-8 rounded-full" />
                <span className="font-medium hidden sm:inline">{headerDisplayName}</span>
                <img src={logo.dropdown} alt="" className="w-4 h-4" />
              </button>

              {isDropdownOpen && (
                <div ref={dropdownRef} className="absolute right-0 mt-3 w-48 bg-white rounded-b-md shadow-lg py-1 z-50">
                  <button
                    onClick={() => setIsLogout(!isLogout)}
                    className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-grey"
                    Country
                    Admin
                    Management
                  >
                    {strings.signout}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <aside
        className={`fixed left-0 top-[4rem] z-20 h-[calc(100vh-4rem)] bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } w-56`}
      >
        <div className="h-full overflow-y-auto">
          <nav className="px-4 py-4 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive ? "text-primary bg-blue-50" : "text-gray-700 hover:bg-gray-50 hover:text-primary"
                    }`}
                >
                  {item.icon}
                  <span>{item.title}</span>
                  {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></span>}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>
      <main
        className={`flex-1 overflow-y-auto transition-all duration-300 ${isSidebarOpen ? (isMobile ? "ml-0" : "ml-56") : "ml-0"} mt-[3rem]`}
      >
        <div className="p-4 sm:p-6">
          <Outlet />
        </div>

        {isLogout && <LogoutModel handleConfirmLogOut={handleConfirmLogOut} handleCancelLogout={handleCancelLogout} />}
      </main>
    </div>
  );
};

export default DashboardLayout;
